import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const sections = [
  {
    number: "1",
    title: "Information We Collect",
    intro:
      "We collect the following types of information to provide and improve our Service:",
    subsections: [
      {
        title: "a. Personal Information",
        bullets: [
          "Name",
          "Email address",
          "Phone number",
          "Login credentials",
          "Profile photo (optional)",
        ],
      },
      {
        title: "b. User-Generated Content",
        bullets: [
          "Reviews and ratings",
          "Photos",
          "Comments",
          "Business information (for business owners)",
        ],
      },
      {
        title: "c. Device & Usage Data",
        bullets: [
          "IP address",
          "Device type (iOS, Android)",
          "Operating system",
          "App version",
          "Browser information",
          "Location data (only if you grant permission)",
          "Log data (time, date, features used, crashes)",
        ],
      },
      {
        title: "d. Cookies & Tracking Technologies",
        bullets: ["Cookies", "Analytics tools", "Session tokens"],
      },
    ],
  },
  {
    number: "2",
    title: "How We Use Your Information",
    bullets: [
      "Create and manage your account",
      "Allow you to post reviews, photos, and comments",
      "Display local businesses and personalized content",
      "Improve performance and reliability",
      "Communicate updates and security alerts",
      "Verify listings and prevent fraud",
      "Enforce our Terms and Conditions",
    ],
    boldText: "We do not sell your personal data to third parties.",
  },
  {
    number: "3",
    title: "How We Share Your Information",
    subsections: [
      {
        title: "a. Public Content",
        paragraphs: [
          "Reviews, photos, ratings, and comments may be publicly visible within the Biznep platform.",
        ],
      },
      {
        title: "b. Service Providers",
        paragraphs: [
          "Trusted third-party vendors such as hosting, analytics, and cloud storage providers who are required to protect your data.",
        ],
      },
      {
        title: "c. Legal Requirements",
        paragraphs: [
          "We may disclose information to comply with Nepali law, court orders, law enforcement requests, or to protect legal rights.",
        ],
      },
      {
        title: "d. Business Transfers",
        paragraphs: [
          "If Biznep is involved in a merger, acquisition, or asset transfer, your information may be transferred.",
        ],
      },
    ],
  },
  {
    number: "4",
    title: "Data Security",
    paragraphs: [
      "We use technical and organizational measures to protect your data. However, no system is completely secure.",
    ],
  },
  {
    number: "5",
    title: "Your Rights & Choices",
    bullets: [
      "Access your personal data",
      "Update or correct information",
      "Delete your account and associated data",
      "Disable cookies",
      "Withdraw location permissions",
    ],
    paragraphs: [
      "Contact us at support@biznep.net to make a request.",
    ],
  },
  {
    number: "6",
    title: "Data Retention",
    paragraphs: [
      "We retain data only as long as necessary to provide services, comply with legal obligations, resolve disputes, and enforce terms. Some content may remain visible unless removal is requested.",
    ],
  },
  {
    number: "7",
    title: "Children’s Privacy",
    paragraphs: [
      "Biznep is not intended for children under 16. We do not knowingly collect their data.",
    ],
  },
  {
    number: "8",
    title: "Third-Party Links",
    paragraphs: [
      "We are not responsible for third-party privacy practices. Review their policies separately.",
    ],
  },
  {
    number: "9",
    title: "International Data Transfer",
    paragraphs: [
      "Your information may be processed in countries with different data protection laws. By using Biznep, you consent to such transfers.",
    ],
  },
  {
    number: "10",
    title: "Updates to This Privacy Policy",
    paragraphs: [
      "We may update this policy from time to time. Continued use means acceptance.",
    ],
  },
  {
    number: "11",
    title: "Contact Information",
    paragraphs: ["Email: support@biznep.net"],
  },
];

function BulletList({ items }) {
  return (
    <ul className="mt-3 space-y-2">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3">
          <span className="mt-[9px] h-[6px] w-[6px] shrink-0 rounded-full bg-[#D32323]" />
          <span className="text-[16px] leading-[1.65] text-[#59677c]">
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

function PolicySection({ section }) {
  return (
    <section className="border-b border-[#e8edf3] py-8 last:border-b-0">
      <h2 className="text-[22px] font-bold leading-[1.3] tracking-[-0.3px] text-[#17233b]">
        {section.number}. {section.title}
      </h2>

      {section.intro && (
        <p className="mt-4 text-[16px] leading-[1.7] text-[#59677c]">
          {section.intro}
        </p>
      )}

      {section.paragraphs?.map((paragraph) => (
        <p
          key={paragraph}
          className="mt-4 text-[16px] leading-[1.7] text-[#59677c]"
        >
          {paragraph}
        </p>
      ))}

      {section.bullets && <BulletList items={section.bullets} />}

      {section.boldText && (
        <p className="mt-5 text-[16px] font-semibold leading-[1.7] text-[#333f52]">
          {section.boldText}
        </p>
      )}

      {section.subsections?.map((subsection) => (
        <div key={subsection.title} className="mt-7">
          <h3 className="text-[17px] font-semibold leading-[1.4] text-[#29364d]">
            {subsection.title}
          </h3>

          {subsection.paragraphs?.map((paragraph) => (
            <p
              key={paragraph}
              className="mt-2 text-[16px] leading-[1.7] text-[#59677c]"
            >
              {paragraph}
            </p>
          ))}

          {subsection.bullets && (
            <BulletList items={subsection.bullets} />
          )}
        </div>
      ))}
    </section>
  );
}

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      {/* EXISTING WEBSITE NAVBAR */}
      <Navbar />

      {/* PRIVACY POLICY CONTENT */}
      <main className="w-full bg-[#fafbff]">
        <div className="mx-auto w-full max-w-[1100px] px-5 py-12 sm:px-6 sm:py-14 lg:px-8 lg:py-16">
          
          {/* PAGE HEADER */}
          <div className="mb-10 border-b border-[#e3e9f0] pb-8">
            <h1 className="text-[36px] font-extrabold leading-[1.15] tracking-[-1px] text-[#10203b] sm:text-[40px]">
              Privacy Policy — Biznep
            </h1>

            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-[14px] text-[#718096]">
              <span>Effective Date: 11/10/2025</span>
              <span>Last Updated: 11/10/2025</span>
            </div>
          </div>

          {/* INTRODUCTION */}
          <div className="mb-2">
            <p className="text-[16px] leading-[1.75] text-[#59677c]">
              Biznep ("we", "our", or "us") is committed to protecting your
              privacy. This Privacy Policy explains how we collect, use,
              store, and safeguard your personal information when you use
              the Biznep mobile application, website, or any related
              services (collectively, the "Service").
            </p>

            <p className="mt-5 text-[16px] leading-[1.75] text-[#59677c]">
              By using Biznep, you agree to the practices described in this
              Privacy Policy. If you do not agree, please discontinue use
              of our Service.
            </p>
          </div>

          {/* POLICY SECTIONS */}
          <div className="mt-2">
            {sections.map((section) => (
              <PolicySection
                key={section.number}
                section={section}
              />
            ))}
          </div>
        </div>
      </main>

      {/* EXISTING WEBSITE FOOTER */}
      <Footer />
    </div>
  );
}